import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DropDown from '../../components/dropdown/DropDown';
import DatePicker from '../../components/datepicker/DatePicker';
import Icon from '../../components/icon/Icon';
import { formatCuit, cuitMask } from '../../utils/Masks';
import { formatDateOnlyLocal } from '../../utils/dateOnly';

interface ProtectorData {
  company_name: string;
  cuit: string;
  email: string;
  registration_date: string;
  document_type_id: number;
  document_type_name: string;
  legal_entity_type_id: number;
  legal_entity_type_name: string;
  meeting_minute_type_id: number;
  meeting_minute_type_name: string;
  minute_number: string;
}

interface FormValues {
  company_name: string;
  cuit: string;
  email: string;
  registration_date: string;
  document_type_id: number | null;
  legal_entity_type_id: number | null;
  meeting_minute_type_id: number | null;
  minute_number: string;
}

interface FormMetadata {
  document_types: Array<{ id: number; name: string }>;
  legal_entity_types: Array<{ id: number; name: string }>;
  meeting_minute_types: Array<{ id: number; name: string }>;
}

interface ProtectorDetailsBasicSectionProps {
  protector: ProtectorData;
  isEditMode?: boolean;
  formValues?: FormValues | null;
  metadata?: FormMetadata | null;
  hasMoras?: boolean;
  validationErrors?: string[];
  minuteNumberError?: string | null;
  onFormChange?: (values: FormValues) => void;
  onEditClick?: () => void;
  onMinuteNumberErrorClear?: () => void;
}

const ProtectorDetailsBasicSection = ({ 
  protector,
  isEditMode = false,
  formValues,
  metadata,
  hasMoras = false,
  validationErrors = [],
  minuteNumberError,
  onFormChange,
  onEditClick,
  onMinuteNumberErrorClear
}: ProtectorDetailsBasicSectionProps) => {
  
  const formatDate = (dateString: string) => formatDateOnlyLocal(dateString);

  const updateFormValues = (updates: Partial<FormValues>) => {
    if (formValues && onFormChange) {
      onFormChange({ ...formValues, ...updates });
    }
  };

  const handleCuitChange = (value: string) => {
    const selectedDocType = metadata?.document_types?.find(
      (type) => type.id === formValues?.document_type_id
    );
    
    if (selectedDocType?.name?.toUpperCase() === 'CUIT') {
      const maskedValue = cuitMask(value);
      updateFormValues({ cuit: maskedValue });
    } else {
      const numericValue = value.replace(/\D/g, '');
      updateFormValues({ cuit: numericValue });
    }
  };

  const documentTypeItems = metadata?.document_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedDocumentTypeName = () => {
    const selectedType = metadata?.document_types?.find(
      (type) => type.id === formValues?.document_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleDocumentTypeChange = (selectedLabel: string) => {
    const type = metadata?.document_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ document_type_id: type.id });
      
      if (type.name?.toUpperCase() === 'CUIT' && formValues?.cuit?.trim()) {
        const maskedValue = cuitMask(formValues.cuit);
        updateFormValues({ cuit: maskedValue, document_type_id: type.id });
      }
    }
  };

  const legalEntityTypeItems = metadata?.legal_entity_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedLegalEntityTypeName = () => {
    const selectedType = metadata?.legal_entity_types?.find(
      (type) => type.id === formValues?.legal_entity_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleLegalEntityTypeChange = (selectedLabel: string) => {
    const type = metadata?.legal_entity_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ legal_entity_type_id: type.id });
    }
  };

  const meetingMinuteTypeItems = metadata?.meeting_minute_types?.map((type) => ({
    label: type.name,
    disabled: false,
  })) || [];

  const getSelectedMeetingMinuteTypeName = () => {
    const selectedType = metadata?.meeting_minute_types?.find(
      (type) => type.id === formValues?.meeting_minute_type_id
    );
    return selectedType ? selectedType.name : 'Seleccionar';
  };

  const handleMeetingMinuteTypeChange = (selectedLabel: string) => {
    const type = metadata?.meeting_minute_types?.find((t) => t.name === selectedLabel);
    if (type) {
      updateFormValues({ meeting_minute_type_id: type.id });
    }
  };

  if (!isEditMode) {
    const detailItems = [
      {
        label: 'Razón social',
        value: protector.company_name || '-',
      },
      {
        label: 'Tipo de documento',
        value: protector.document_type_name || '-',
      },
      {
        label: 'Documento',
        value: protector.cuit ? formatCuit(protector.cuit) : '-',
      },
      {
        label: 'Email',
        value: protector.email || '-',
      },
      {
        label: 'Fecha de registro',
        value: formatDate(protector.registration_date),
      },
      {
        label: 'Entidad legal',
        value: protector.legal_entity_type_name || '-',
      },
      {
        label: 'Tipo de acta',
        value: protector.meeting_minute_type_name || '-',
      },
      {
        label: 'Número de acta',
        value: protector.minute_number || '-',
      },
    ];

    return (
      <Card margin={{ bottom: '4' }}>
        <Card
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          style={{ marginBottom: '12px' }}
        >
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos del protector
          </H>
          <button
            type='button'
            onClick={onEditClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <Icon
              name='edit_outline'
              variant='info'
              modifier='regular'
              width={20}
              height={20}
              cursor='pointer'
            />
            <P type='2' weight='medium' variant='info' withMargin={false}>
              Editar
            </P>
          </button>
        </Card>

        <Card
          display='grid'
          style={{
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            columnGap: '16px',
            rowGap: '12px',
          }}
        >
          {detailItems.map((item) => (
            <Card
              key={item.label}
              display='flex'
              gap='4px'
              style={{ minWidth: 0 }}
            >
              <P
                type='2'
                variant='secondary-2'
                modifier='strong'
                weight='bold'
                withMargin={false}
                style={{ whiteSpace: 'nowrap' }}
              >
                {item.label}:
              </P>
              <P
                type='2'
                variant='secondary-2'
                modifier='strong'
                weight='medium'
                withMargin={false}
              >
                {item.value}
              </P>
            </Card>
          ))}
        </Card>
      </Card>
    );
  }

  return (
    <Card margin={{ bottom: '4' }}>
      <Card
        display='flex'
        alignItems='center'
        style={{ marginBottom: '12px' }}
      >
        <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
          Datos del protector
        </H>
      </Card>

      {validationErrors.length > 0 && (
        <Card margin={{ bottom: '3' }}>
          {validationErrors.map((err, index) => (
            <P key={index} variant='danger'>{err}</P>
          ))}
        </Card>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        <Input
          label="Razón social *"
          value={formValues?.company_name || ''}
          onChange={(e) => updateFormValues({ company_name: e.target.value })}
          size="md"
        />
        <DropDown
          id="document_type"
          label="Tipo de documento *"
          title={getSelectedDocumentTypeName()}
          items={documentTypeItems}
          onChange={handleDocumentTypeChange}
          size="md"
          marginTop="4px"
        />
        <Input
          label="Documento *"
          value={formValues?.cuit || ''}
          onChange={(e) => handleCuitChange(e.target.value)}
          disabled
          size="md"
        />
        <Input
          label="Email *"
          value={formValues?.email || ''}
          onChange={(e) => updateFormValues({ email: e.target.value })}
          size="md"
        />
      </div>

      {/*
        Fila 1: solo controles (misma altura). Fila 2: ayuda solo bajo fecha, para no desalinear con align-items:end.
        DropDown aplica marginTop 4px + 4px antes del Label; Input/DatePicker con label no, así que compensamos con marginTop 8px.
      */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          columnGap: '16px',
          rowGap: '4px',
        }}
      >
        <div style={{ marginTop: '8px' }}>
          <DatePicker
            id="protector_registration_date"
            label={`Fecha de registro${!hasMoras ? ' *' : ''}`}
            key={formValues?.registration_date ?? 'empty'}
            defaultValue={formValues?.registration_date || ''}
            onChange={(date: string) => updateFormValues({ registration_date: date })}
            disabled={hasMoras}
            displayFormat="DD/MM/AA"
          />
        </div>
        <DropDown
          id="legal_entity_type"
          label="Entidad legal *"
          title={getSelectedLegalEntityTypeName()}
          items={legalEntityTypeItems}
          onChange={handleLegalEntityTypeChange}
          size="md"
          marginTop="4px"
        />
        <DropDown
          id="meeting_minute_type"
          label="Tipo de acta *"
          title={getSelectedMeetingMinuteTypeName()}
          items={meetingMinuteTypeItems}
          onChange={handleMeetingMinuteTypeChange}
          size="md"
          marginTop="4px"
        />
        <div style={{ marginTop: '8px' }}>
          <Input
            label="Número de acta *"
            value={formValues?.minute_number || ''}
            onChange={(e) => {
              updateFormValues({ minute_number: e.target.value });
              if (minuteNumberError && onMinuteNumberErrorClear) onMinuteNumberErrorClear();
            }}
            size="md"
            inputStatus={minuteNumberError ? 'error' : 'normal'}
            message={minuteNumberError || undefined}
          />
        </div>
        <div style={{ gridColumn: '1 / 2' }}>
          {hasMoras && (
            <P type="4" variant="neutral" modifier="weak" withMargin={false}>
              No editable (protector con moras)
            </P>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ProtectorDetailsBasicSection;
